import { Filter, settings } from '@pixi/core'
import { BasePixiFilter } from '@scenes/engine/utils/PixijsFilters/BasePixiFilter'
import { DropShadowHDFilter } from '@scenes/engine/utils/PixijsFilters/DropShadowHDFilter'
import { ColorCubeMapFilter } from '@scenes/engine/utils/PixijsFilters/ColorMapCube/ColorMapCubeFilter'
import { BasicFilterFactory, FilterPresetType } from '@scenes/engine/utils/PixijsFilters/BasicFilterFactory'
import { AdjustmentFilterFactory } from '@scenes/engine/utils/PixijsFilters/AdjustmentFilterFactory'
import { OutlineHDFilter } from './PixijsFilters/OutlineHD/OutlineHdFilter'
import { ColorMap } from '../common/interfaces'
import { ColorOverlayFilter } from 'pixi-filters'
import { MediaImageRepository } from '../objects/media-repository/media_image_repository'
import { CanvasLayerOutlineEffect } from '@/interfaces/CanvasLayerOutlineEffect'
import { CanvasLayerShadowEffect } from '@/interfaces/CanvasLayerShadowEffect'
import ApiService from '@services/api'
import * as PIXI from 'pixi.js'
import { Rectangle } from "@/shared/sync/mediaRepository/rectangle"
import { Size } from "@/shared/sync/mediaRepository/size"
import { Inset } from '@/shared/sync/mediaRepository/inset'
import { Point } from "@/shared/sync/mediaRepository/point"
import { MediaImageType } from '@/shared/sync/mediaRepository/media_image_type'
import { MediaImageRepositoryProvider } from '../objects/media-repository/MediaImageRepositoryProvider'
class CanvasImageRenderer {
  adjustmentFilterFactory = new AdjustmentFilterFactory()
  outlineFilter = new OutlineHDFilter()
  shadowFilter = new DropShadowHDFilter()

  blurToImageRatio = 3;

  private static instance: CanvasImageRenderer
  private imageCache: Map<string, HTMLImageElement> = new Map();
  private textureCache: Map<string, PIXI.Texture> = new Map();
  private static MAX_CACHE_SIZE = 20;

  public static getInstance(): CanvasImageRenderer {
    if (!CanvasImageRenderer.instance) {
      CanvasImageRenderer.instance = new CanvasImageRenderer()
    }

    return CanvasImageRenderer.instance
  }

  public clearCache() {
    this.imageCache.clear();
    this.textureCache.forEach(t => t.destroy(true));
    this.textureCache.clear();
  }

  private getCacheKey(guid: string, stateId: string, type: MediaImageType): string {
    return `${guid}_${stateId}_${type}`;
  }

  private async getCachedImage(guid: string, stateId: string, type: MediaImageType): Promise<HTMLImageElement | null> {
    const key = this.getCacheKey(guid, stateId, type);
    if (this.imageCache.has(key)) {
      return this.imageCache.get(key)!;
    }

    const src = await MediaImageRepositoryProvider.getEditingProjectInstance().getImage(guid, stateId, type);
    if (!src) return null;

    const img = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.loadImage(src);
    if (img) {
      if (this.imageCache.size >= CanvasImageRenderer.MAX_CACHE_SIZE) {
        const firstKey = this.imageCache.keys().next().value;
        this.imageCache.delete(firstKey);
      }
      this.imageCache.set(key, img);
    }
    return img;
  }

  isDefaultFilter(filterName: string | null): boolean {
    let defaultFilterName = "normal";
    if (!filterName) {
      return true;
    }
    return filterName.toLowerCase() == defaultFilterName;
  }
  
  isAdjustDefault(adjustValues: [string: number]): boolean {
    let keys = Array.from(this.adjustmentFilterFactory.rangeValueForProperty.keys())
    for (let key of keys) {
      let defaultValues = [this.adjustmentFilterFactory.rangeValueForProperty.get(key).defaultValue, this.adjustmentFilterFactory.builder.defaultPlaceholder];
      if (adjustValues[key] &&  defaultValues.indexOf(adjustValues[key]) === -1)  {
        return false
      }
    }
    return true
  }

  async getFilters(imageElement, colorMaps: ColorMap[]): Promise<BasePixiFilter[]> {
    let pixijsFilters: Filter[] = []
    let pixijsPaddings: Size[] = []
    let pixijsInsets: Inset[] = []
    let pixijsShared: boolean[] = []

    let zeroPadding = new Size(0, 0)
    let zeroInset = new Inset(0, 0, 0, 0)

    let w = imageElement.width
    let h = imageElement.height

    let filterName: string = imageElement.filter
    let filterIntensity = imageElement.filterIntensity
    let colorMap

    let overlay = imageElement.effects?.overlay
    if (overlay) {
      const colorNumber = new PIXI.Color(overlay.color).toNumber()
      const colorOverlayFilter = new ColorOverlayFilter(colorNumber, overlay.alpha)
      pixijsFilters.push(colorOverlayFilter)
      pixijsPaddings.push(zeroPadding)
      pixijsInsets.push(zeroInset)
      pixijsShared.push(false)
    }

    if (filterName) {
      if (filterName.startsWith('CI')) {
        let filterType = mapCIFilterToFilterPreset(filterName)
        let factory = new BasicFilterFactory(filterType)
        let filter = await factory.geFilter(filterIntensity)

        pixijsFilters.push(filter)
        pixijsPaddings.push(zeroPadding)
        pixijsInsets.push(zeroInset)
        pixijsShared.push(false) 
      }
      const packID = Number(filterName.split('_')[0])
      if (packID && !isNaN(packID)) {
        colorMap = colorMaps.find(cm => cm.id === packID)
      }
    }

    if (colorMap && colorMap.image) {
      // Use stable path for caching instead of signed URL
      const url = ApiService.prepareUrl(colorMap.image);
      const cacheKey = colorMap.image; 
      
      let texture = this.textureCache.get(cacheKey);
      if (!texture) {
        // Enforce cache limit
        if (this.textureCache.size >= CanvasImageRenderer.MAX_CACHE_SIZE) {
          const firstKey = this.textureCache.keys().next().value;
          const textureToDestroy = this.textureCache.get(firstKey);
          textureToDestroy?.destroy(true);
          this.textureCache.delete(firstKey);
        }

        texture = await PIXI.Texture.fromURL(url);
        this.textureCache.set(cacheKey, texture);
      }
      
      if (texture) {
        // Create new filter instance to avoid race conditions, but reuse texture
        const colorMapFilter = new ColorCubeMapFilter(texture, false, filterIntensity);
        
        pixijsFilters.push(colorMapFilter)
        pixijsPaddings.push(zeroPadding)
        pixijsInsets.push(zeroInset)
        pixijsShared.push(false) 
      } else {
        console.log('No texture is created')
      }
    }
    // imageElement.effects.adjustments = {brightness: 2, contrast: 1, saturation: 1};
    let adjustments = imageElement.adjustments
    if (adjustments) {
      adjustments = Object.assign(adjustments, {
        imageHeight: h,
        imageWidth: w,
      })
      let filters = this.adjustmentFilterFactory.parse(adjustments)
      for (let filter of filters) {
        pixijsFilters.push(filter)
        pixijsPaddings.push(zeroPadding)
        pixijsInsets.push(zeroInset)
        pixijsShared.push(true) // Adjustment filters are shared
      }
    }

    let outline = imageElement.effects?.outline
    if (outline && outline.thickness > 0) {
      // outline is relative to image size
      // let thickness = Math.floor((outline.thickness / 100) * Math.max(h, w))
      let thickness = Math.floor(outline.thickness * Math.max(h, w))
      
      // Create new instance to avoid race conditions
      const outlineFilter = new OutlineHDFilter();
      outlineFilter.thickness = thickness
      outlineFilter.color = parseInt(outline.color.substring(1), 16)
      
      pixijsFilters.push(outlineFilter)
      let padding = new Size(thickness * 2, thickness * 2)
      pixijsPaddings.push(padding)

      let inset = new Inset(thickness, thickness, thickness, thickness)
      pixijsInsets.push(inset)
      pixijsShared.push(false) 
    }

    let shadow = imageElement.effects?.shadow
    if (shadow) {
      let outlineThickness = outline?.thickness ?? 0;
      // let outlinePercent = 1 / (1 + outlineThickness * 2);
      let outlinePercent = (1 + outlineThickness * 2);
      let maxEdge = Math.max(h * outlinePercent, w * outlinePercent)
      let adjustedBlurSize = Math.floor(shadow.blur * maxEdge);
      
      // offsets are relative to image size
      let offsetX 
      let offsetY 
      
      if(shadow.offsetX === null || shadow.offsetX === undefined || shadow.offsetY === null || shadow.offsetY === undefined) {
        let angle = shadow.angle;
        offsetX = Math.floor(Math.cos(angle ?? 0) * shadow.distance * maxEdge)
        offsetY = Math.floor(Math.sin(angle ?? 0) * shadow.distance * maxEdge)
      } else {
        offsetX = Math.floor(shadow.offsetX * maxEdge)
        offsetY = Math.floor(shadow.offsetY * maxEdge)
      }

      // Create new instance to avoid race conditions
      const shadowFilter = new DropShadowHDFilter();
      shadowFilter.blur = adjustedBlurSize
      shadowFilter.color = parseInt(shadow.color.substring(1), 16)
      shadowFilter.alpha = shadow.opacity
      shadowFilter.offset = {
        x: offsetX,
        y: offsetY,
      }

      let padding = new Size(
        Math.abs(offsetX) + adjustedBlurSize * 2,
        Math.abs(offsetY) + adjustedBlurSize * 2
      )
      let inset = new Inset(
        adjustedBlurSize - Math.min(0, offsetX),
        adjustedBlurSize - Math.min(0, offsetY),
        adjustedBlurSize + Math.max(0, offsetX),
        adjustedBlurSize + Math.max(0, offsetY)
      )

      pixijsFilters.push(shadowFilter)
      pixijsPaddings.push(padding)
      pixijsInsets.push(inset)
      pixijsShared.push(false) 
    }

    let filters: BasePixiFilter[] = []
    for (let i = 0; i < pixijsFilters.length; i++) {
      let f = new BasePixiFilter(pixijsFilters[i], pixijsPaddings[i], pixijsInsets[i], pixijsShared[i])
      filters.push(f)
    }
    return filters;
  }

  async render(imageElement, canvasSize: Size, colorMaps: ColorMap[], filterBackend?: fabric.WebglFilterBackend, forceOriginalImages?: boolean) {
    let filters: BasePixiFilter[] = []
    
    try {
      filters = await this.getFilters(imageElement, colorMaps)
      
      if (imageElement.isLatest){
        imageElement.set('isLatest', false);

        let sourceBounds = new Rectangle(0, 0, imageElement.width, imageElement.height);
        
        const guid = imageElement.bazaartGuid;
        const stateId = imageElement.layerAssetStateId;

        if (!forceOriginalImages) {
          let fittedImage = await this.getCachedImage(guid, stateId, MediaImageType.fitted);
          let fittedMask = await this.getCachedImage(guid, stateId, MediaImageType.fittedMask);
          
          if (fittedImage && fittedMask) {
            const cacheKey = this.getCacheKey(guid, stateId, MediaImageType.fittedMasked);
            let fittedMasked = this.imageCache.get(cacheKey);
            
            if (!fittedMasked) {
              let fittedMaskedSrc = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.applyMaskToImage(fittedImage, fittedMask)
              fittedMasked = await MediaImageRepositoryProvider.getEditingProjectInstance()._mediaImageRepositoryProcessing.loadImage(fittedMaskedSrc);
              if (fittedMasked) {
                this.imageCache.set(cacheKey, fittedMasked);
              }
            }
            
            if (fittedMasked) {
              imageElement.setElement(fittedMasked);
            }
          } else {
            let original = await this.getCachedImage(guid, stateId, MediaImageType.original);
            if (original) {
              imageElement.setElement(original);
            }
          }
        } else {
          let original = await this.getCachedImage(guid, stateId, MediaImageType.original);
          if (original) {
            imageElement.setElement(original);
          }
        }

        let shadowEffects = new CanvasLayerShadowEffect();
        let targetBoundsWithoutShadow = shadowEffects.revertEffectBounds(imageElement,  canvasSize, false, sourceBounds);
        let nonSymmetricOffset = new Point(targetBoundsWithoutShadow.x, targetBoundsWithoutShadow.y) ;

        let outlineEffects = new CanvasLayerOutlineEffect();
        let targetSizeWithoutOutline = outlineEffects.revertEffectBounds(imageElement, canvasSize, false, targetBoundsWithoutShadow);
        
        
        let originalSizeOnCanvas = {
          // @ts-ignore
          width: targetSizeWithoutOutline.width * imageElement.scaleX * imageElement._filterScalingX / canvasSize.width,
          // @ts-ignore
          height: targetSizeWithoutOutline.height * imageElement.scaleY * imageElement._filterScalingY / canvasSize.height
        }

        imageElement._filterScalingX = 1;
        imageElement._filterScalingY = 1; 

        // TODO: handle the case in which the element has been rotated before playing around with shadow color

        let offsetX = nonSymmetricOffset.x * imageElement.scaleX * (imageElement.flipX ? -1 : 1)
        let offsetY = nonSymmetricOffset.y * imageElement.scaleY * (imageElement.flipY ? -1 : 1)
        const angleInRadians = (imageElement.angle * Math.PI) / 180;

        const rotatedOffsetX = offsetX * Math.cos(angleInRadians) - offsetY * Math.sin(angleInRadians);
        const rotatedOffsetY = offsetX * Math.sin(angleInRadians) + offsetY * Math.cos(angleInRadians);

        imageElement.left -= rotatedOffsetX;
        imageElement.top -= rotatedOffsetY;

        // imageElement.left -= nonSymmetricOffset.x * imageElement.scaleX
        // imageElement.top -= nonSymmetricOffset.y * imageElement.scaleY;

        imageElement.scaleX = originalSizeOnCanvas.width / (imageElement.width * imageElement._filterScalingX / canvasSize.width);
        imageElement.scaleY = originalSizeOnCanvas.height / (imageElement.height * imageElement._filterScalingY / canvasSize.height);

        imageElement._originalScaleX = imageElement.scaleX;
        imageElement._originalScaleY = imageElement.scaleY;
      }
      imageElement.set('dirty', true)
      
      imageElement.applyFilters(filters, filterBackend, false)
      filters = null // Ownership transferred to StaticImage
    } catch (error) {
      console.error('Failed to render image with filters', error)
      // Cleanup filters on error
      if (filters) {
        filters.forEach(f => f.destroy && f.destroy())
      }
    }
    
    return imageElement
  }
}

const mapCIFilterToFilterPreset = (ciFilterName: string): FilterPresetType => {
  switch (ciFilterName) {
    case 'CIPhotoEffectChrome':
      return FilterPresetType.chrome
    case 'CIPhotoEffectFade':
      return FilterPresetType.fade
    case 'CIPhotoEffectTransfer':
      return FilterPresetType.transfer
    case 'CIPhotoEffectProcess':
      return FilterPresetType.process
    case 'CIPhotoEffectInstant':
      return FilterPresetType.instant
    case 'CIPhotoEffectMono':
      return FilterPresetType.monochrome
    case 'CIPhotoEffectTonal':
      return FilterPresetType.tonal
    case 'CIPhotoEffectNoir':
      return FilterPresetType.noir
    default:
      return FilterPresetType.default
  }
}

export default CanvasImageRenderer
